/* eslint-disable react/require-default-props */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Row, Col, Spin } from 'antd'
import { FormInstance } from 'antd/es/form/Form'
import Form, { Rule } from 'antd/lib/form'
import ID_COMPONENTE from 'config/idComponente'
import { Empresa, Veiculo } from 'Models/cadastros'
import { set, useForm } from 'react-hook-form'
import { Tooltip } from 'react-tooltip'
import { ParamsRequest } from 'Services/__shared/types/resquest'
import WsCadastroService from 'Services/WS_CADASTRO'
import 'react-tooltip/dist/react-tooltip.css'
import IFormEmpresasFilter from 'Views/Cadastros/Empresas/IFormEmpresasFilter'
import IFormVeiculosFilter from 'Views/Cadastros/Veiculos/IFormVeiculosFilter'

import InputPlaca from './InputPlaca'
import ResultadoPesquisa from './ResultadoPesquisa'
import { SelectFieldAnt, SelectItemProps } from './Select'

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: FormInstance<any> //Nesse caso é necessário utilizar o "any" pois o useForm não é tipado
  setVeiculoSelecionado: Dispatch<SetStateAction<Veiculo | undefined>>
  veiculoSelecionado?: Veiculo
  selecionarPrimeiroVeiculoEncontrado?: boolean
  rules?: Rule[]

  //As props abaixo são opcionais, pois o componente pode ser utilizado sem a necessidade de selecionar uma transportadora
  obrigatorioSelecionarTransportadora?: boolean
  setTransportadoraSelecionada?: Dispatch<SetStateAction<Empresa | undefined>>
  transportadoraSelecionada?: Empresa
}

const PesquisaVeiculo: React.FC<IProps> = ({
  form,
  setVeiculoSelecionado,
  veiculoSelecionado,
  selecionarPrimeiroVeiculoEncontrado = false,
  rules,
  obrigatorioSelecionarTransportadora = false,
  setTransportadoraSelecionada,
  transportadoraSelecionada,
}) => {
  const hookForm = useForm()

  const [placa, setPlaca] = useState<string>('')

  const [transportadoras, setTransportadoras] = useState<Empresa[]>([])
  const [selectTransportadoraItens, setSelectTransportadoraItens] = useState<
    SelectItemProps[]
  >([])
  const [cdTransportadoraSelecionada, setCdTransportadoraSelecionada] =
    useState<number>()

  const [veiculos, setVeiculos] = useState<Veiculo[]>()
  const [selectVeiculoItens, setSelectVeiculoItens] = useState<
    SelectItemProps[]
  >([])
  const [cdVeiculoSelecionado, setCdVeiculoSelecionado] = useState<number>()

  const [carregandoPesquisa, setCarregandoPesquisa] = useState<boolean>(false)
  const [resultado, setResultado] = useState<ResultadoPesquisa>()

  const buscaTransportadoras = async (): Promise<void> => {
    const filtros: IFormEmpresasFilter = {
      ativo: true,
    }

    const parametros: ParamsRequest = {
      pageSize: 1000, //TO-DO: Ver se seria boa ideia que o WsCadastro retorne todas as empresas se esse parâmetro não for preenchido
      pageNumber: 1,
    }

    const { data } = await WsCadastroService.empresas.getList(
      ID_COMPONENTE.DOCAS,
      filtros,
      parametros,
    )

    if (data.items && data.items.length > 0) {
      setTransportadoras(data.items)

      setSelectTransportadoraItens(
        data.items.map((item: Empresa): SelectItemProps => {
          return {
            value: item.id.toString(),
            label: item.nome,
          }
        }),
      )
    } else {
      mostraResultado(false, 'Nenhuma transportadora encontrada')
    }
  }

  const validaPlaca = (placa: string): boolean => {
    const regex = /^[A-Z]{3}\d{1,4}$|^[A-Z]{3}\d[A-Z]\d{2}$/ //Placas no formato ABC1234 ou ABC1D23
    return regex.test(placa)
  }

  const buscaVeiculos = async (): Promise<void> => {
    if (validaPlaca(placa)) {
      try {
        const filtros: IFormVeiculosFilter = {
          placa: placa,
          situacao: 'ativo',
        }

        if (cdTransportadoraSelecionada) {
          filtros.cdEntidade = cdTransportadoraSelecionada
        }

        const parametros: ParamsRequest = {
          pageSize: 1000, //TO-DO: Ver se seria boa ideia que o WsCadastro retorne todos os veículos se esse parâmetro não for preenchido
          pageNumber: 1,
        }

        const { data } = await WsCadastroService.veiculos.getList(
          ID_COMPONENTE.DOCAS,
          filtros,
          parametros,
        )

        if (data.items) {
          if (selecionarPrimeiroVeiculoEncontrado && data.items.length > 0) {
            setVeiculos([data.items[0]])
          } else {
            setVeiculos(data.items)
          }
        }

        if (!data.items || data.items.length === 0) {
          mostraResultado(false, 'Nenhum veículo encontrado')
        }
      } catch (erro) {
        mostraResultado(false, 'Ocorreu um erro ao buscar os veículos')
        window.console.error(erro)
      }
    } else {
      mostraResultado(false, 'A placa precisa ter o formato ABC1234 ou ABC1D23')
    }

    setCarregandoPesquisa(false)
  }

  const selecionaTransportadora = (
    cdTransportadora: number,
    removerVeiculoSelecionado = false,
  ): void => {
    const transportadora: Empresa | undefined = transportadoras.find(
      (item: Empresa) => item.id === cdTransportadora,
    )

    if (transportadora && setTransportadoraSelecionada) {
      setTransportadoraSelecionada(transportadora)

      if (removerVeiculoSelecionado && veiculoSelecionado) {
        setVeiculoSelecionado(undefined)
      }
    } else {
      mostraResultado(
        false,
        'Ocorreu um erro ao selecionar a transportadora, atualize a página e tente novamente',
      )
    }
  }

  const preparaTransportadoraSelecionada = (
    cdTransportadora?: number,
  ): void => {
    if (cdTransportadora) {
      selecionaTransportadora(cdTransportadora, true)
    } else if (transportadoraSelecionada && setTransportadoraSelecionada) {
      setTransportadoraSelecionada(undefined)
    }
  }

  const preparaVeiculoSelecionado = (cdVeiculo?: number): void => {
    if (cdVeiculo) {
      const veiculo: Veiculo | undefined = veiculos?.find(
        (item: Veiculo) => item.id === cdVeiculo,
      )

      if (veiculo) {
        setVeiculoSelecionado(veiculo)

        if (transportadoras.length > 0 && setTransportadoraSelecionada) {
          selecionaTransportadora(veiculo.cdEntidade)
        }
      } else {
        mostraResultado(
          false,
          'Ocorreu um erro ao selecionar o veículo, atualize a página e tente novamente',
        )
      }
    } else if (veiculoSelecionado) {
      setVeiculoSelecionado(undefined)
    }
  }

  const verificaSeDesabilitaBotaoPesquisar = (): boolean => {
    return (
      placa.length !== 7 ||
      (obrigatorioSelecionarTransportadora && !transportadoraSelecionada)
    )
  }

  const preparaVeiculos = (): void => {
    if (veiculos) {
      if (veiculos.length <= 1) {
        setCdVeiculoSelecionado(veiculos[0]?.id) //Se o array de veículos tiver apenas um item, já o seleciona. Mas se não tiver nenhum, limpa qualquer seleção anterior
        setSelectVeiculoItens([]) //Se o array de veículos tiver 0 ou 1 item, o Select não deve ser exibido
        mostraResultado(veiculos.length > 0, 'Nenhum veículo encontrado')
      } else {
        setCdVeiculoSelecionado(undefined)
        setSelectVeiculoItens(
          veiculos
            .sort(
              (a: Veiculo, b: Veiculo) =>
                a.nomeEntidade.localeCompare(b.nomeEntidade), //Classifica os veículos por nome da transportadora em ordem alfabética
            )
            .map((item: Veiculo): SelectItemProps => {
              return {
                value: item.id.toString(),
                label: item.nomeEntidade, //Todos os veículos possuem a mesma placa, então o "label" é o nome da transportadora
              }
            }),
        )
        window.alert(
          'Foram encontrados mais de um veículo com a placa informada, selecione a transportadora desejada',
        )
      }
    }
  }

  const verificaVeiculoSelecionado = (): void => {
    if (veiculoSelecionado) {
      mostraResultado(true)
    } else if (resultado) {
      setResultado(undefined)
    }
  }

  const mostraResultado = (
    sucesso: boolean,
    mensagemEmCasoDeErro?: string,
  ): void => {
    let mensagem: string
    let cor: string

    if (sucesso) {
      mensagem = 'Veículo encontrado'
      cor = 'green'
    } else {
      mensagem = mensagemEmCasoDeErro ?? 'Erro'
      cor = 'red'
    }

    setResultado(new ResultadoPesquisa(sucesso, mensagem, cor))
  }

  const verificaSeMostraSelectVeiculos = (): boolean => {
    return selectVeiculoItens.length > 1
  }

  const verificaSeMostraSelectTransportadoras = (): boolean => {
    return !verificaSeMostraSelectVeiculos() && !!setTransportadoraSelecionada //Força conversão para boolean
  }

  const calculaTamanhoColPlaca = (): number => {
    return verificaSeMostraSelectTransportadoras() ||
      verificaSeMostraSelectVeiculos()
      ? 12
      : 24
  }

  useEffect(() => {
    if (setTransportadoraSelecionada) {
      buscaTransportadoras()
    }
  }, [])

  useEffect(() => {
    if (cdVeiculoSelecionado) {
      setCdVeiculoSelecionado(undefined)
      setVeiculoSelecionado(undefined)
    }

    if (veiculos) {
      setVeiculos(undefined)
      setSelectVeiculoItens([])
    }

    form.resetFields(['cdVeiculo'])
  }, [placa])

  useEffect(() => {
    if (carregandoPesquisa) {
      buscaVeiculos()
    }
  }, [carregandoPesquisa])

  useEffect(() => {
    preparaTransportadoraSelecionada(cdTransportadoraSelecionada)
  }, [cdTransportadoraSelecionada])

  useEffect(() => {
    preparaVeiculoSelecionado(cdVeiculoSelecionado)
  }, [cdVeiculoSelecionado])

  useEffect(() => {
    preparaVeiculos()
  }, [veiculos])

  useEffect(() => {
    verificaVeiculoSelecionado()
  }, [veiculoSelecionado])

  return (
    <Row gutter={16}>
      {verificaSeMostraSelectVeiculos() && (
        <Col span={12}>
          <SelectFieldAnt
            methods={hookForm}
            scope="form-veiculo"
            label="Transportadora:"
            name="cdVeiculo"
            showSearch={true}
            items={selectVeiculoItens}
            onChangeValue={(cdVeiculoStr: string) =>
              setCdVeiculoSelecionado(window.parseInt(cdVeiculoStr))
            }
            rules={rules}
          />
        </Col>
      )}

      {verificaSeMostraSelectTransportadoras() && (
        <Col span={12}>
          {selectTransportadoraItens.length > 0 ? (
            <SelectFieldAnt
              methods={hookForm}
              scope="form-veiculo"
              label="Transportadora:"
              name="cdTransportadora"
              showSearch={true}
              emptyValue={true}
              items={selectTransportadoraItens}
              onChangeValue={(cdTransportadoraStr: string) =>
                setCdTransportadoraSelecionada(
                  window.parseInt(cdTransportadoraStr),
                )
              }
              rules={rules}
            />
          ) : (
            <Form.Item
              label="Transportadora:"
              name="cdTransportadora"
              rules={rules}
            >
              <Spin />
            </Form.Item>
          )}
        </Col>
      )}

      <Col span={calculaTamanhoColPlaca()}>
        <Row gutter={0}>
          <Col span={18}>
            <InputPlaca
              name="placa"
              label="Placa:"
              placa={placa}
              setPlaca={setPlaca}
              rules={rules}
              disabled={
                obrigatorioSelecionarTransportadora &&
                setTransportadoraSelecionada &&
                !transportadoraSelecionada
              }
            />
          </Col>

          <Col span={6}>
            <div style={{ marginTop: '30px' }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => setCarregandoPesquisa(true)}
                disabled={verificaSeDesabilitaBotaoPesquisar()}
                loading={carregandoPesquisa}
              />

              {resultado && (
                <>
                  <Tooltip
                    anchorId={'resultadoPesquisaVeiculo'}
                    html={resultado.mensagem}
                    place="top"
                    style={{ zIndex: 1 }}
                  />

                  <span
                    id="resultadoPesquisaVeiculo"
                    style={{ color: resultado.cor, marginLeft: '5px' }}
                  >
                    {resultado.sucesso ? <CheckOutlined /> : <CloseOutlined />}
                  </span>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default PesquisaVeiculo
